import { WalletConnectMoleculeReatom } from "@/app/wallet-connect/store.reatom"
import { LoggerMolecule } from "@/instrumentation/logger/logger-molecule"
import { type EventContext, Flow } from "@/instrumentation/logger/types"
import * as amplitude from "@amplitude/analytics-browser"
import type { CollectionMiniature } from "@anaconda/core/nft-collection/types"
import type { CurrencyId } from "@anaconda/core/price/types"
import {
  type BigNumberValue,
  type CollectionBidRequest,
  collectionBidFactoryReatom,
} from "@rarible_int/anaconda_sdk/ethereum"
import { sonnerToast } from "@rarible_int/anaconda_ui_components"
import { action, withAsync, withCallHook, wrap } from "@reatom/core"
import Big from "big.js"
import { molecule } from "bunshi"
import type { Address } from "viem"
import { BlockchainBalanceSdkProviderMoleculeReatom } from "../../blockchains/blockchain-balance-sdk-provider.reatom"
import { EvmSdkContainerMoleculeReatom } from "../../blockchains/evm-sdk-container-molecule.reatom"
import { CollectionBidToast } from "../../toasts/collection-bid-toast"
import {
  TransactionEvent,
  getTransactionErrorMessage,
} from "../events/transaction-event"

export interface CollectionBidActionParams {
  collection: CollectionMiniature
  amount: number
  currency: CurrencyId
  price: BigNumberValue
}

export interface CollectionBidActionResult {
  txId?: string
  collection: CollectionMiniature
}

export const CollectionBidTransactionMoleculeReatom = molecule((mol) => {
  const logger = mol(LoggerMolecule)
  const { sdkContainerAtom } = mol(EvmSdkContainerMoleculeReatom)
  const { address: addressAtom } = mol(WalletConnectMoleculeReatom)
  const { balanceSDKAtom } = mol(BlockchainBalanceSdkProviderMoleculeReatom)

  const collectionBidActionFactory = () => {
    const collectionBidAction = action(
      async ({
        collection,
        amount,
        currency: currencyId,
        price,
      }: CollectionBidActionParams) => {
        const container = sdkContainerAtom()
        const balanceSDK = balanceSDKAtom()
        const from = addressAtom()

        if (!from) {
          throw new Error("Wallet is not connected")
        }

        const { id: collectionId, blockchain } = collection

        // Validate balance
        let wrappedBalance: string | undefined
        let nativeBalance: string | undefined

        try {
          if (balanceSDK) {
            wrappedBalance = await wrap(
              balanceSDK.getTokenBalanceByCurrency({
                blockchain,
                currencyId: currencyId,
                address: from as Address,
              }),
            )
            nativeBalance = await wrap(
              balanceSDK.getNativeBalance({
                blockchain,
                address: from as Address,
              }),
            )
          }
        } catch (error) {
          console.warn("Failed to get balance for validation:", error)
          wrappedBalance = undefined
          nativeBalance = undefined
        }

        if (wrappedBalance && nativeBalance) {
          const wethBalance = Big(wrappedBalance)
          const ethBalance = Big(nativeBalance)
          const totalBalance = ethBalance.plus(wethBalance)
          const priceAmount = Big(price.toString())

          if (priceAmount.gt(totalBalance)) {
            throw new Error("Error: insufficient funds for collection offer")
          }
        }

        // Execute collection bid via Reatom SDK
        const collectionBid = collectionBidFactoryReatom(container)

        const request: CollectionBidRequest = {
          blockchain,
          currencyId: currencyId,
          from: from as Address,
          priceValue: price,
          originFees: [],
          amount,
          collectionId,
        }

        const result = await wrap(collectionBid(request))

        // Show success toast (even without txId for off-chain signatures)
        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <CollectionBidToast.Success
            collection={collection}
            id={id}
            // biome-ignore lint/style/noNonNullAssertion: txId should always exist for successful transactions
            transactionId={result.txId!}
          />
        ))

        return { txId: result.txId, collection }
      },
      "collectionBidAction",
    ).extend(
      withAsync(),

      // onCall: Called when action starts (replaces onMutate)
      withCallHook(async (values, [params]) => {
        const { collection, amount, currency, price } = params

        sonnerToast.custom(
          (id) => (
            <CollectionBidToast.Pending collection={collection} id={id} />
          ),
          {
            duration: Number.POSITIVE_INFINITY,
          },
        )

        const { id: collectionId, blockchain } = collection

        const baseTransactionEventContext: EventContext = {
          flow: Flow.MakeOffer,
          collection: collectionId,
          blockchain,
        }

        const context: EventContext = {
          ...baseTransactionEventContext,
          amount: amount.toString(),
          currency,
          price: price.toString(),
        }

        logger?.api.pushEvent(TransactionEvent.Started, context)
        amplitude.track(TransactionEvent.Started, context)
      }),
    )

    // onFulfill: Called on success (replaces onSuccess)
    collectionBidAction.onFulfill.extend(
      withCallHook(({ payload, params }) => {
        const { txId, collection } = payload
        const { amount, currency, price } = params[0]

        const { id: collectionId, blockchain } = collection

        const baseTransactionEventContext: EventContext = {
          flow: Flow.MakeOffer,
          collection: collectionId,
          blockchain,
        }

        const context: EventContext = {
          ...baseTransactionEventContext,
          amount: amount.toString(),
          currency,
          price: price.toString(),
        }

        if (txId) {
          context.transactionId = txId
        }

        logger?.api.pushEvent(TransactionEvent.Completed, context)
        amplitude.track(TransactionEvent.Completed, context)
      }),
    )

    // onReject: Called on error (replaces onError)
    collectionBidAction.onReject.extend(
      withCallHook(({ error, params: [paramsValue] }) => {
        console.error("Collection bid failed:", error)

        const { collection, amount, currency, price } = paramsValue

        const { id: collectionId, blockchain } = collection

        const baseTransactionEventContext: EventContext = {
          flow: Flow.MakeOffer,
          collection: collectionId,
          blockchain,
        }

        const context: EventContext = {
          ...baseTransactionEventContext,
          amount: amount.toString(),
          currency,
          price: price.toString(),
          error: getTransactionErrorMessage(error),
        }

        logger?.api.pushEvent(TransactionEvent.Failed, context)
        amplitude.track(TransactionEvent.Failed, context)

        sonnerToast.dismiss()
        sonnerToast.custom((id) => (
          <CollectionBidToast.Error
            collection={collection}
            id={id}
            error={error as Error}
          />
        ))
      }),
    )

    return collectionBidAction
  }

  return { collectionBidActionFactory }
})
